/**
 * Material information, Parts A, B and C, as a buyer reads them.
 *
 * The keys here are the same keys as `backend/crm/material_info.py`, and a
 * test binds the two lists so neither can gain a field the other has never
 * heard of. The LABELS are deliberately not the same: the Python ones are
 * prompts written for an agent filling a form ("Rights of way and
 * easements"), and these are column headings on a public page ("Rights &
 * easements"). Same question, different reader.
 *
 * Why any of this is published: National Trading Standards guidance, read
 * with the Consumer Protection from Unfair Trading Regulations, makes
 * omitting information a buyer needs an unfair practice, and it is enforced
 * against the agent. Foundation collected all three parts from the start and
 * published none of them, because the public API never sent the fields.
 */

// Part B applies to every property: a house has drainage whether or not
// anybody wrote it down, so a blank here is simply missing.
export const PART_B = [
  ["construction_type", "Construction"],
  ["parking", "Parking"],
  ["heating", "Heating"],
  ["electricity_supply", "Electricity"],
  ["water_supply", "Water"],
  ["drainage", "Drainage"],
  ["broadband_type", "Broadband"],
  ["mobile_coverage", "Mobile"],
];

// Part C is different: most houses have no covenant and have never flooded,
// and for those the right published answer is "none known", not silence.
export const PART_C = [
  ["flood_risk", "Flood risk"],
  ["restrictive_covenants", "Restrictions"],
  ["rights_of_way", "Rights & easements"],
  ["planning_permission", "Planning"],
  ["building_safety", "Building safety"],
  ["accessibility", "Accessibility"],
  ["listed_or_conservation", "Listed or conservation area"],
  ["mining_area", "Mining & land stability"],
  ["coastal_erosion", "Coastal erosion"],
];

const said = (v) => Boolean(String(v ?? "").trim());

/** Part B rows worth printing: whatever the agency has actually stated. */
export function partBRows(info, propertyType) {
  const out = [];
  if (said(propertyType)) out.push(["Type", String(propertyType).trim()]);
  for (const [key, label] of PART_B) {
    if (key === "broadband_type") {
      const speed = Number(info?.broadband_speed_mbps || 0);
      const kind = String(info?.broadband_type ?? "").trim();
      // An agent with the speed off a checker and not the technology has
      // still told a buyer more than a blank does.
      const text = speed
        ? (kind ? `${kind} · up to ${speed.toLocaleString()} Mbps` : `Up to ${speed.toLocaleString()} Mbps`)
        : kind;
      if (text) out.push([label, text]);
      continue;
    }
    if (said(info?.[key])) out.push([label, String(info[key]).trim()]);
  }
  return out;
}

/**
 * Part C rows, and the distinction the whole thing turns on.
 *
 * `checked` names the items somebody has actually looked into. An empty
 * detail on a checked item means "none apply", which is a real answer and
 * the commonest one. An item nobody has looked at is left out entirely:
 * printing "None known" against a question no one asked would be the agency
 * stating something it does not know, on the page it is liable for.
 */
export function partCRows(info) {
  const checked = new Set(info?.checked || []);
  const out = [];
  for (const [key, label] of PART_C) {
    const detail = String(info?.[key] ?? "").trim();
    if (detail) out.push([label, detail]);
    else if (checked.has(key)) out.push([label, "None known"]);
  }
  return out;
}
